import { Button, Col, Row, Space, Typography } from "antd";
import {
  CustomerServiceOutlined,
  FileProtectOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LoginOutlined,
  MailOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import AuthBrand from "components/auth/AuthBrand";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const publicPageHeaderGap = 20;

const oliveebLinkStyle = {
  color: OLIVEEB_COLORS.oliveebBlue,
  fontWeight: 800,
  textDecoration: "none",
};

const OliveebLink = () => (
  <a
    href="https://oliveeb.com"
    target="_blank"
    rel="noreferrer"
    style={oliveebLinkStyle}
  >
    Oliveeb
  </a>
);

const shinyStyles = `
  .ovacus-product-logo-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 14px 22px;
    border-radius: 32px;
    background: linear-gradient(145deg, rgba(255,255,255,0.98), rgba(246,255,246,0.92));
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    box-shadow:
      0 16px 34px rgba(39,174,48,0.18),
      inset 0 1px 0 rgba(255,255,255,0.96);
    overflow: hidden;
  }
  .ovacus-product-logo-wrap::before {
    content: "";
    position: absolute;
    top: -30px;
    left: -36px;
    width: 92px;
    height: 92px;
    border-radius: 999px;
    background: rgba(255,255,255,0.64);
  }
  .ovacus-product-bg-shine {
    position: absolute;
    top: 82px;
    right: max(-90px, calc((100vw - 1180px) / 2 - 118px));
    width: 760px;
    height: 390px;
    pointer-events: none;
    z-index: 0;
  }
  .ovacus-product-circle-green,
  .ovacus-product-circle-gold {
    position: absolute;
    border-radius: 999px;
    filter: blur(1px);
  }
  .ovacus-product-circle-green {
    left: 0;
    top: 42px;
    width: 485px;
    height: 285px;
    background:
      radial-gradient(circle at 38% 44%, rgba(255,255,255,0.86) 0%, rgba(246,255,246,0.74) 36%, rgba(231,246,239,0.36) 66%, transparent 100%);
  }
  .ovacus-product-circle-gold {
    right: 0;
    top: 0;
    width: 425px;
    height: 245px;
    background:
      radial-gradient(circle at 44% 46%, rgba(255,255,255,0.88) 0%, rgba(255,253,235,0.70) 40%, rgba(248,193,11,0.10) 70%, transparent 100%);
  }
`;

const publicMenu = [
  { key: "home", label: "Home", path: "/", icon: <HomeOutlined /> },
  { key: "about", label: "About", path: "/about", icon: <InfoCircleOutlined /> },
  { key: "contact", label: "Contact Us", path: "/contact-us", icon: <MailOutlined /> },
  { key: "privacy", label: "Privacy Policy", path: "/privacy-policy", icon: <FileProtectOutlined /> },
  { key: "help", label: "Help", path: "/help", icon: <CustomerServiceOutlined /> },
];

const pageContent = {
  home: {
    eyebrow: "Ovacus for individuals, startups, and enterprise teams",
    title: "Project management that keeps delivery focused, governed, and moving.",
    deck: "Give leadership cleaner visibility while teams keep the daily work simple and actionable.",
    body:
      "Ovacus gives teams a clear place to plan work, organize projects, coordinate tasks, manage workspaces, and keep delivery decisions visible. It is built for organizations that need structure without slowing people down.",
    highlights: [
      {
        title: "Projects and tasks in one managed flow",
        text: "Reduce status meetings with a shared delivery view everyone can trust.",
      },
      {
        title: "Workspace context for teams and organizations",
        text: "Keep ownership, members, subscriptions, and decisions aligned by workspace.",
      },
      {
        title: "Product testing support for validation-ready delivery",
        text: "Connect project execution with testing signals before work reaches customers.",
      },
    ],
  },
  about: {
    eyebrow: "About Ovacus",
    title: "Built for teams that need project clarity across departments.",
    deck: "Designed for managed enterprise teams that need speed, accountability, and fewer handoff gaps.",
    body: (
      <>
        Ovacus brings project management, team coordination, testing workflows, access control,
        and workspace governance into a single <OliveebLink /> experience. Enterprise teams can
        manage day-to-day work while keeping enough structure for accountability and scale.
      </>
    ),
    highlights: [
      {
        title: "Managed team workspaces",
        text: "Organize people, permissions, and operating context without losing flexibility.",
      },
      {
        title: "Project, task, and delivery visibility",
        text: "Give managers and contributors a cleaner picture of what is moving and what needs attention.",
      },
      {
        title: "Validation workflows for product teams",
        text: "Bring quality checkpoints closer to the delivery process.",
      },
    ],
  },
  contact: {
    eyebrow: "Contact us",
    title: (
      <>
        Talk with <OliveebLink /> about bringing Ovacus to your organization.
      </>
    ),
    deck: "A focused conversation can map Ovacus to your delivery model, governance needs, and team structure.",
    body:
      "Whether you are coordinating internal delivery, product teams, or managed client work, we can help you evaluate how Ovacus fits your enterprise workflow.",
    highlights: [
      {
        title: "Enterprise onboarding conversations",
        text: "Explore rollout paths that fit your organization instead of forcing a generic setup.",
      },
      {
        title: "Workspace and access planning",
        text: "Design workspace structure, ownership, and permissions before adoption grows.",
      },
      {
        title: "Product testing workflow review",
        text: "Identify where validation can support delivery decisions and release confidence.",
      },
    ],
  },
  privacy: {
    eyebrow: "Privacy Policy",
    title: "A privacy-aware workspace for serious teams.",
    deck: "Built around authenticated access and clear workspace context for teams that handle real business work.",
    body: (
      <>
        Ovacus is designed around managed access, authenticated workspaces, and clear user context.
        Privacy and security practices should be reviewed with your <OliveebLink /> agreement,
        organization policies, and applicable service terms.
      </>
    ),
    action: {
      label: "Read our Privacy Policy",
      href: "https://oliveeb.com/privacy_policy.html#policy-sections",
    },
    highlights: [
      {
        title: "Authenticated access",
        text: "Keep product work inside signed-in team experiences with clear user identity.",
      },
      {
        title: "Workspace-aware context",
        text: "Separate team and organization activity so business context remains easier to manage.",
      },
      {
        title: "Role and privilege aligned controls",
        text: "Support stronger governance as teams and responsibilities expand.",
      },
    ],
  },
  help: {
    eyebrow: "Help",
    title: "Support for teams adopting Ovacus.",
    deck: "Help resources are shaped around the workflows teams use most: projects, workspaces, and validation.",
    body:
      "Get help with signing in, setting up workspaces, managing projects, coordinating members, and using product testing workflows in a managed team setting.",
    highlights: [
      {
        title: "Workspace setup guidance",
        text: "Start with the right account, organization, members, and subscription context.",
      },
      {
        title: "Project and task workflow help",
        text: "Help teams structure work so progress and blockers are easier to see.",
      },
      {
        title: "Testing and validation support",
        text: "Use validation workflows to improve confidence before delivery decisions are final.",
      },
    ],
  },
};

const resolvePageKey = (pathname) => {
  if (pathname.endsWith("/about")) return "about";
  if (pathname.endsWith("/contact") || pathname.endsWith("/contact-us")) return "contact";
  if (pathname.endsWith("/privacy") || pathname.endsWith("/privacy-policy")) return "privacy";
  if (pathname.endsWith("/help")) return "help";
  return "home";
};

const currentYear = new Date().getFullYear();

const scaleCards = [
  {
    title: "One-person team",
    text: "Keep engineering projects, tasks, and validation organized even when you are planning, building, and shipping on your own.",
    color: OLIVEEB_COLORS.oliveebDarkBlue,
    background: OLIVEEB_COLORS.oliveebBlueSoft,
    border: OLIVEEB_COLORS.oliveebBlueSoft,
  },
  {
    title: "Growing delivery team",
    text: "Coordinate members, workspaces, agile tasks, reviews, and product testing as collaboration expands.",
    color: OLIVEEB_COLORS.oliveebDarkGreen,
    background: OLIVEEB_COLORS.greenSoft2,
    border: OLIVEEB_COLORS.oliveebGreenSoft,
  },
  {
    title: "Large enterprise organization",
    text: "Support governed SDLC delivery, access control, workspace context, and release confidence across many teams.",
    color: OLIVEEB_COLORS.oliveebDarkRed,
    background: OLIVEEB_COLORS.goldSoft,
    border: OLIVEEB_COLORS.goldBorder,
  },
];

const PublicProductPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeKey = resolvePageKey(pathname);
  const content = pageContent[activeKey];

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          `linear-gradient(135deg, ${OLIVEEB_COLORS.white} 0%, ${OLIVEEB_COLORS.white} 34%, ${OLIVEEB_COLORS.greenSoft2} 68%, ${OLIVEEB_COLORS.oliveebGreenSoft} 100%)`,
        color: OLIVEEB_COLORS.textPrimary,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{shinyStyles}</style>
      <div className="ovacus-product-bg-shine" aria-hidden="true">
        <span className="ovacus-product-circle-green" />
        <span className="ovacus-product-circle-gold" />
      </div>
      <div
        style={{
          maxWidth: 1180,
          width: "100%",
          margin: "0 auto",
          padding: "22px 24px 48px",
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col>
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{
                border: 0,
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <span className="ovacus-product-logo-wrap">
                <AuthBrand size={140} />
              </span>
            </button>
          </Col>
          <Col>
            <Space wrap size={8}>
              {publicMenu.map((item) => (
                <Button
                  key={item.key}
                  icon={item.icon}
                  onClick={() => navigate(item.path)}
                  style={{
                    borderRadius: 999,
                    fontWeight: 800,
                    color:
                      activeKey === item.key
                        ? OLIVEEB_COLORS.white
                        : OLIVEEB_COLORS.oliveebDarkBlue,
                    borderColor:
                      activeKey === item.key
                        ? OLIVEEB_COLORS.oliveebGreen
                        : OLIVEEB_COLORS.oliveebBlueSoft,
                    background:
                      activeKey === item.key
                        ? OLIVEEB_COLORS.oliveebGreen
                        : OLIVEEB_COLORS.white,
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                icon={<LoginOutlined />}
                onClick={() => navigate(paths.simpleLogin)}
                style={{
                  borderRadius: 999,
                  fontWeight: 900,
                  color: OLIVEEB_COLORS.oliveebGreen,
                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                }}
              >
                Sign In
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[32, 24]} align="middle" style={{ marginTop: publicPageHeaderGap }}>
          <Col xs={24} lg={14}>
            <Space direction="vertical" size={18} style={{ width: "100%" }}>
              <Text
                style={{
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                }}
              >
                {content.eyebrow}
              </Text>
              <Title
                level={1}
                style={{
                  margin: 0,
                  color: OLIVEEB_COLORS.oliveebDarkGreen,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  maxWidth: 820,
                }}
              >
                {content.title}
              </Title>
              <Text
                style={{
                  display: "block",
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontSize: 14,
                  lineHeight: 1.7,
                  maxWidth: 680,
                }}
              >
                {content.deck}
              </Text>
              <Paragraph
                style={{
                  color: OLIVEEB_COLORS.oliveebBlue,
                  fontSize: 18,
                  lineHeight: 1.8,
                  maxWidth: 760,
                }}
              >
                {content.body}
              </Paragraph>
              <Space wrap size={12}>
                {content.action?.href ? (
                  <Button
                    type="primary"
                    icon={<FileProtectOutlined />}
                    href={content.action.href}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      height: 46,
                      borderRadius: 999,
                      fontWeight: 900,
                      background: OLIVEEB_COLORS.oliveebGreen,
                      borderColor: OLIVEEB_COLORS.oliveebGreen,
                      boxShadow: "0 12px 24px rgba(39,174,48,0.22)",
                    }}
                  >
                    {content.action.label}
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    onClick={() => navigate(paths.getStarted)}
                    style={{
                      height: 46,
                      borderRadius: 999,
                      fontWeight: 900,
                      background: OLIVEEB_COLORS.oliveebGreen,
                      borderColor: OLIVEEB_COLORS.oliveebGreen,
                      boxShadow: "0 12px 24px rgba(39,174,48,0.22)",
                    }}
                  >
                    Get Started
                  </Button>
                )}
                <Button
                  icon={<MailOutlined />}
                  onClick={() => navigate("/contact-us")}
                  style={{
                    height: 46,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: OLIVEEB_COLORS.oliveebBlue,
                    borderColor: OLIVEEB_COLORS.oliveebBlue,
                    background: OLIVEEB_COLORS.white,
                  }}
                >
                  Contact Us
                </Button>
              </Space>
            </Space>
          </Col>

          <Col xs={24} lg={10}>
            <div
              style={{
                borderRadius: 28,
                padding: 24,
                background: OLIVEEB_COLORS.white,
                border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                boxShadow: "0 24px 64px rgba(6, 105, 2, 0.12)",
              }}
            >
              <Space
                direction="vertical"
                size={14}
                style={{ width: "100%" }}
              >
                {content.highlights.map((item, index) => (
                  <div
                    key={item.title}
                    style={{
                      padding: 16,
                      borderRadius: 18,
                      border: `1px solid ${
                        index === 0
                          ? OLIVEEB_COLORS.oliveebBlueSoft
                          : index === 1
                            ? OLIVEEB_COLORS.oliveebGreenSoft
                            : OLIVEEB_COLORS.goldBorder
                      }`,
                      background:
                        index === 0
                          ? OLIVEEB_COLORS.oliveebBlueSoft
                          : index === 1
                            ? OLIVEEB_COLORS.greenSoft2
                            : OLIVEEB_COLORS.goldSoft,
                      }}
                    >
                    <Space direction="vertical" size={5}>
                      <Text
                        style={{
                          color:
                            index === 0
                              ? OLIVEEB_COLORS.oliveebDarkBlue
                              : index === 1
                                ? OLIVEEB_COLORS.oliveebDarkGreen
                                : OLIVEEB_COLORS.oliveebDarkRed,
                          fontWeight: 900,
                          fontSize: 16,
                        }}
                      >
                        {item.title}
                      </Text>
                      <Text
                        style={{
                          color:
                            index === 0
                              ? OLIVEEB_COLORS.oliveebDarkBlue
                              : index === 1
                                ? OLIVEEB_COLORS.oliveebDarkGreen
                                : OLIVEEB_COLORS.oliveebDarkRed,
                          fontSize: 12,
                          lineHeight: 1.55,
                        }}
                      >
                        {item.text}
                      </Text>
                    </Space>
                  </div>
                ))}
              </Space>
            </div>
          </Col>
        </Row>

        <section
          style={{
            marginTop: 36,
            marginBottom: 36,
            padding: 24,
            borderRadius: 28,
            background: "rgba(255,255,255,0.78)",
            border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
            boxShadow: "0 18px 48px rgba(6, 105, 2, 0.09)",
          }}
        >
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <Text
                style={{
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: 12,
                }}
              >
                Scales with your team
              </Text>
              <Title
                level={3}
                style={{
                  margin: "6px 0 0",
                  color: OLIVEEB_COLORS.oliveebDarkGreen,
                  fontWeight: 900,
                }}
              >
                From one-person teams to large enterprise organizations.
              </Title>
            </div>
            <Row gutter={[14, 14]}>
              {scaleCards.map((item) => (
                <Col xs={24} md={8} key={item.title}>
                  <div
                    style={{
                      height: "100%",
                      padding: 16,
                      borderRadius: 18,
                      background: item.background,
                      border: `1px solid ${item.border}`,
                    }}
                  >
                    <Text strong style={{ display: "block", color: item.color, fontSize: 15 }}>
                      {item.title}
                    </Text>
                    <Text
                      style={{
                        display: "block",
                        marginTop: 6,
                        color: item.color,
                        fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {item.text}
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Space>
        </section>

        <div
          style={{
            marginTop: "auto",
            paddingTop: 18,
            borderTop: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            color: OLIVEEB_COLORS.textMuted,
            fontSize: 12,
          }}
        >
          <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
            Copyright © {currentYear} Oliveeb LLC. All rights reserved.
          </Text>
          <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
            Ovacus is an Oliveeb product for managed project delivery.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PublicProductPage;
